import {Component, computed, inject, OnInit, signal, Signal, TemplateRef, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatDialog} from "@angular/material/dialog"
import {MatIconModule} from "@angular/material/icon"
import {MatInputModule} from "@angular/material/input"
import {MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {ContentTypeModel, MutationUpdateTemplateInput} from "@generated"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {TemplateThumbnailRenderingService} from "@app/common/services/template-thumbnail-rendering/template-thumbnail-rendering.service"
import {
    DeleteTemplateDetailsGQL,
    TemplateDetailsCreateTemplateGQL,
    TemplateDetailsCreateTemplateRevisionGQL,
    TemplateDetailsDeleteTemplateRevisionGQL,
    TemplateDetailsGQL,
    UpdateTemplateDetailsGQL,
    TemplateDetailsFragment,
    TemplateDetailsCachedGltfGQL,
} from "@app/templates/template-details/template-details.generated"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {HintComponent} from "@common/components/hint/hint.component"
import {CopyLegacyIdButtonComponent} from "@common/components/buttons/copy-legacy-id-button/copy-legacy-id-button.component"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {BooleanLabelSelectComponent} from "@common/components/inputs/select/boolean-label-select/boolean-label-select.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {TagSelectComponent} from "@common/components/inputs/select/tag-select/tag-select.component"
import {SectionContentComponent} from "@common/components/item"
import {SectionComponent} from "@common/components/item/item-details/section/section.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateReturningDataAndErrors, mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {Enums} from "@enums"
import {UtilsService} from "@legacy/helpers/utils"
import {AssociatedJobsTableComponent} from "@platform/components/jobs/associated-jobs-table/associated-jobs-table.component"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {CommentSectionComponent} from "@platform/components/details/comment-section/comment-section.component"
import {FilesSectionComponent} from "@platform/components/details/files-section/files-section.component"
import {GalleryImageComponent} from "@platform/components/details/gallery-image/gallery-image.component"
import {TemplatesSectionComponent} from "@platform/components/details/templates-section/templates-section.component"
import {TitleSectionComponent} from "@platform/components/details/title-section/title-section.component"
import {CornersLayoutComponent} from "@platform/components/layouts/corners-layout/corners-layout.component"
import {DetailsDialogLayoutComponent} from "@platform/components/layouts/details-dialog-layout/details-dialog-layout.component"
import {MaterialRevisionsHistoryDialogComponent} from "@app/platform/components/materials/material-revisions-history-dialog/material-revisions-history-dialog.component"
import {TemplateRevisionsHistoryDialogComponent} from "@app/templates/template-revisions-history-dialog/template-revisions-history-dialog.component"
import {ArticleIdSectionComponent} from "@platform/components/details/article-id-section/article-id-section.component"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {PaymentStateLabelComponent} from "@platform/components/shared/payment-state-label/payment-state-label.component"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {BakeTextureService} from "@app/template-editor/services/bake-texture.service"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {GltfExportService} from "@app/common/services/gltf-export/gltf-export.service"
import {StlExportService} from "@app/common/services/stl-export/stl-export.service"
import {FilesService} from "@app/common/services/files/files.service"
import {deserializeTemplateGraph, Parameters} from "@cm/template-nodes"
import {ThreeTemplateSceneProviderComponent} from "@app/template-editor/components/three-template-scene-provider/three-template-scene-provider.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-details",
    templateUrl: "./template-details.component.html",
    styleUrls: ["./template-details.component.scss"],
    imports: [
        MatMenuModule,
        ButtonComponent,
        HintComponent,
        MatTooltipModule,
        MatInputModule,
        MatSelectModule,
        FormsModule,
        DetailsDialogLayoutComponent,
        TagSelectComponent,
        MatIconModule,
        MatButtonModule,
        GalleryImageComponent,
        RoutedDialogComponent,
        FilesSectionComponent,
        TitleSectionComponent,
        OrganizationSelectComponent,
        CommentSectionComponent,
        BooleanLabelSelectComponent,
        SectionContentComponent,
        SectionComponent,
        AssociatedJobsTableComponent,
        CornersLayoutComponent,
        CopyLegacyIdButtonComponent,
        TimeAgoPipe,
        TemplatesSectionComponent,
        TemplateRevisionsHistoryDialogComponent,
        ArticleIdSectionComponent,
        LoadingSpinnerIconComponent,
        PaymentStateLabelComponent,
        ThreeTemplateSceneProviderComponent,
    ],
    providers: [SceneManagerService, TemplateThumbnailRenderingService, MeshGeometryService, BakeTextureService],
})
export class TemplateDetailsComponent extends BaseDetailsComponent<TemplateDetailsFragment, Omit<MutationUpdateTemplateInput, "id">> implements OnInit {
    override _contentTypeModel = ContentTypeModel.Template
    protected readonly ContentTypeModel = ContentTypeModel
    readonly templateDetails = inject(TemplateDetailsGQL)
    readonly updateTemplateDetails = inject(UpdateTemplateDetailsGQL)
    readonly deleteTemplateDetails = inject(DeleteTemplateDetailsGQL)
    override _fetchItem = fetchThrowingErrors(this.templateDetails)
    override _updateItem = mutateThrowingErrors(this.updateTemplateDetails)
    override _deleteItem = mutateReturningDataAndErrors(this.deleteTemplateDetails)
    readonly $materialRevisionHistoryDialog = viewChild<TemplateRef<MaterialRevisionsHistoryDialogComponent>>("templateRevisionHistoryDialog")

    readonly templateDetailsCreateTemplate = inject(TemplateDetailsCreateTemplateGQL)
    readonly templateDetailsCreateTemplateRevision = inject(TemplateDetailsCreateTemplateRevisionGQL)
    readonly deleteTemplateRevisionGql = inject(TemplateDetailsDeleteTemplateRevisionGQL)
    readonly templateDetailsCachedGltfGql = inject(TemplateDetailsCachedGltfGQL)

    // 3D Download services and state
    readonly gltfExportService = inject(GltfExportService)
    readonly stlExportService = inject(StlExportService)
    readonly sceneManagerService = inject(SceneManagerService)
    private threeSceneManagerService: ThreeSceneManagerService | undefined
    readonly $isDownloadingGltf = signal(false)
    readonly $isDownloadingStl = signal(false)
    readonly $needsConfirmationToClose: Signal<boolean> = computed(() => this.$isDownloadingGltf() || this.$isDownloadingStl())

    readonly $multiThumbnailIds = computed(() => {
        const item = this.$item()
        if (item?.galleryImage?.id) {
            return null
        }
        return this.$item()?.latestRevision?.renderedImages?.map(({id}) => id) ?? null
    })

    readonly matDialog = inject(MatDialog)
    readonly utils = inject(UtilsService)
    readonly snackBar = inject(MatSnackBar)
    readonly templateRenderingService = inject(TemplateThumbnailRenderingService)
    readonly notificationsService = inject(NotificationsService)
    readonly organizations = inject(OrganizationsService)

    readonly $renderThumbnailButtonDisabled = signal(false)

    showRevisionHistoryDialog() {
        const materialRevisionHistoryDialog = this.$materialRevisionHistoryDialog()
        if (!materialRevisionHistoryDialog) {
            throw new Error("Missing materialRevisionHistoryDialog ref in template")
        }
        const item = this.$item()
        this.matDialog.open(materialRevisionHistoryDialog, {
            data: {template: {id: item?.id, organization: {id: item?.organization.id}}},
            width: "800px",
        })
    }

    deleteConfirmationMessage() {
        const item = this.$item()
        return (
            (item ? `The Template '${item.name}'` : "This Template") +
            " will be deleted. This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?"
        )
    }

    addOrEditTemplateRevision() {
        return this.$item()?.latestRevision ? this.editTemplateRevision() : this.addTemplateRevision()
    }

    addTemplateRevision() {
        void this.router.navigate(["revisions", "add"], {relativeTo: this.route, queryParamsHandling: "preserve"})
    }

    editTemplateRevision() {
        const latestRevisionId = this.$item()?.latestRevision?.id
        if (latestRevisionId) {
            this.router.navigate(["revisions", latestRevisionId, "edit"], {
                relativeTo: this.route,
                queryParamsHandling: "preserve",
            })
        }
    }

    viewTemplate() {
        void this.router.navigate(["view"], {relativeTo: this.route, queryParamsHandling: "preserve"})
    }

    async openJob(job: {id?: string | null} | undefined) {
        if (job?.id) {
            await this.router.navigate(["/jobs", job.id], {queryParamsHandling: "preserve"})
        }
    }

    async renderThumbnail() {
        // TBD: better UX for progress handling?
        this.$renderThumbnailButtonDisabled.set(true)
        try {
            const templateFragment = this.$item()
            if (templateFragment) {
                this.notificationsService.showInfo("Template thumbnail: processing")
                await this.templateRenderingService.renderTemplateThumbnail(templateFragment)
                this.notificationsService.showInfo("Template thumbnail: job submitted")
            } else {
                throw new Error("Template thumbnail: missing template fragment")
            }
        } catch (error) {
            this.notificationsService.showError(error)
        } finally {
            this.$renderThumbnailButtonDisabled.set(false)
        }
    }

    readonly $getEditButtonLabel = computed(() => (this.$item()?.latestRevision ? "Edit" : "+ Edit"))

    copyTemplate(template: TemplateDetailsFragment) {
        const targetOrganization = this.organizations.$current()
        void this.notifications.withUserFeedback(
            async () => {
                const {createTemplate: newTemplate} = await mutateThrowingErrors(this.templateDetailsCreateTemplate)({
                    input: {
                        name: `Copy of ${template.name}`,
                        organizationId: targetOrganization?.id ?? template.organization.id,
                        templateType: template.type,
                        comment: template.comment,
                        public: template.public,
                        state: Enums.TemplateState.Draft,
                    },
                })
                await mutateThrowingErrors(this.templateDetailsCreateTemplateRevision)({
                    input: {
                        templateId: newTemplate.id,
                        graph: template.latestRevision?.graph,
                        completed: false,
                    },
                })
                void this.router.navigate(["..", newTemplate.id], {
                    relativeTo: this.route,
                    queryParamsHandling: "preserve",
                })
            },
            {
                error: "Cannot create new template.",
            },
        )
    }

    deleteTemplateRevision(templateRevision: {id: string}) {
        const dialogRef = this.matDialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Discard revision",
                message:
                    "The last template revision including all the geometry and material configurations will be deleted. " +
                    "This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?",
                confirmLabel: "Discard revision",
                cancelLabel: "Cancel",
                isDestructive: true,
            },
        })
        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: async (confirmed) => {
                    if (!confirmed) {
                        return
                    }
                    await mutateThrowingErrors(this.deleteTemplateRevisionGql)({
                        id: templateRevision.id,
                    })
                    this.refresh.item(this.$item())
                    this.notifications.showInfo("Revision discarded.")
                },
                error: () => this.notifications.showError("Cannot discard revision."),
            })
    }

    async downloadGltf() {
        if (!this.threeSceneManagerService) throw new Error("ThreeSceneManagerService not set")

        const template = this.$item()
        if (!template?.latestRevision) return

        this.$isDownloadingGltf.set(true)
        try {
            // First check for cached GLTF
            const {dataObjectAssignments} = await fetchThrowingErrors(this.templateDetailsCachedGltfGql)({
                templateRevisionId: template.latestRevision.id,
            })

            if (dataObjectAssignments.length > 0 && dataObjectAssignments[0]?.dataObject?.downloadUrl) {
                // Use cached file
                const dataObject = dataObjectAssignments[0].dataObject
                FilesService.downloadFile(dataObject.originalFileName ?? "model.glb", dataObject.downloadUrl)
            } else {
                // Fall back to real-time export
                const graph = template.latestRevision.graph
                if (!graph) {
                    throw new Error("Template has no graph data for export")
                }

                const templateGraph = deserializeTemplateGraph(graph)
                const emptyParameters = new Parameters({parameters: {}})
                this.sceneManagerService.$templateGraph.set(templateGraph)
                this.sceneManagerService.$instanceParameters.set(emptyParameters)
                this.sceneManagerService.compileTemplate()
                await this.threeSceneManagerService.sync(true)

                const binaryGltf = await this.gltfExportService.exportGltfFile(templateGraph, emptyParameters, undefined, this.threeSceneManagerService, "web")
                FilesService.downloadFile(`${template.name ?? "model"}.glb`, new Blob([binaryGltf as Uint8Array<ArrayBuffer>]))
            }
            this.notificationsService.showInfo("GLTF download complete")
        } catch (error) {
            this.notificationsService.showError(error)
        } finally {
            this.$isDownloadingGltf.set(false)
        }
    }

    async downloadStl() {
        const template = this.$item()
        if (!template?.latestRevision) return

        this.$isDownloadingStl.set(true)
        try {
            const graph = template.latestRevision.graph
            if (!graph) {
                throw new Error("Template has no graph data for export")
            }

            const templateGraph = deserializeTemplateGraph(graph)
            const emptyParameters = new Parameters({parameters: {}})
            this.sceneManagerService.$templateGraph.set(templateGraph)
            this.sceneManagerService.$instanceParameters.set(emptyParameters)
            this.sceneManagerService.compileTemplate()

            const zippedStls = await this.stlExportService.exportStlFiles(templateGraph, emptyParameters, undefined, this.sceneManagerService)
            FilesService.downloadFile(`${template.name ?? "model"}-stl-export.zip`, zippedStls)
            this.notificationsService.showInfo("STL download complete")
        } catch (error) {
            this.notificationsService.showError(error)
        } finally {
            this.$isDownloadingStl.set(false)
        }
    }

    setThreeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService | undefined) {
        if (threeSceneManagerService) {
            threeSceneManagerService.$ambientLight.set(false)
        }

        this.threeSceneManagerService = threeSceneManagerService
    }

    protected readonly DialogSize = DialogSize
}
